/**
 * DriveZone — OTP Verification Page
 * Route: /verify-otp
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ClipboardEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2, RotateCcw, MailOpen } from 'lucide-react'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type { AuthUser } from '@/stores/authStore'
import { AxiosError } from 'axios'

const OTP_LENGTH = 6
const RESEND_COOLDOWN = 60

export default function OTPPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const email     = (location.state as { email?: string })?.email ?? ''

  const { tempToken, setUser, clearTempToken, isAuthenticated } = useAuthStore()

  const [digits, setDigits]       = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')
  const [cooldown, setCooldown]   = useState(RESEND_COOLDOWN)
  const [resending, setResending] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (!tempToken && !isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [tempToken, isAuthenticated, navigate])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timer); return 0 }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const submitOTP = useCallback(async (code: string) => {
    if (!tempToken) return
    setError('')
    setLoading(true)

    try {
      const res = await api.post<{ user: AuthUser; message: string }>(
        '/api/auth/verify-otp/',
        { otp_code: code, temp_token: tempToken }
      )
      setUser(res.data.user)
      navigate('/app/search', { replace: true })
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: string }>
      const errorCode = axiosErr.response?.data?.error

      if (errorCode === 'token_expired') {
        setError('Your session expired. Please log in again.')
        setTimeout(() => navigate('/login', { replace: true }), 2000)
      } else if (axiosErr.response?.status === 429) {
        setError('Too many attempts. Please wait before trying again.')
      } else {
        setError('Invalid or expired code. Please try again.')
      }
      setDigits(Array(OTP_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }, [tempToken, setUser, navigate])

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...digits]
    newDigits[index] = digit
    setDigits(newDigits)

    if (digit) {
      if (index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
      if (newDigits.every((d) => d !== '') && newDigits.join('').length === OTP_LENGTH) {
        submitOTP(newDigits.join(''))
      }
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const newDigits = [...digits]
        newDigits[index] = ''
        setDigits(newDigits)
      } else if (index > 0) {
        const newDigits = [...digits]
        newDigits[index - 1] = ''
        setDigits(newDigits)
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    const newDigits = Array(OTP_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i]
    }
    setDigits(newDigits)
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusIndex]?.focus()
    if (pasted.length === OTP_LENGTH) {
      submitOTP(pasted)
    }
  }

  async function handleResend() {
    if (cooldown > 0 || !email || !tempToken) return
    setResending(true)
    setError('')

    try {
      const res = await api.post<{ temp_token: string }>(
        '/api/auth/resend-otp/',
        { email, temp_token: tempToken }
      )
      useAuthStore.getState().setTempToken(res.data.temp_token)
      setDigits(Array(OTP_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
      setCooldown(RESEND_COOLDOWN)
    } catch {
      setError('Could not resend code. Please try again.')
    } finally {
      setResending(false)
    }
  }

  const currentCode = digits.join('')

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)' }}>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="text-center mb-8">
          <img src="/favicon.svg" alt="DriveZone" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-semibold text-white">DriveZone</h1>
          <p className="text-xs font-medium tracking-widest text-sky-100 uppercase mt-2">
            Check your email
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">

          {/* Info */}
          <div className="text-center">
            <MailOpen className="w-10 h-10 text-sky-500 mx-auto mb-3" />
            {email ? (
              <p className="text-sm text-slate-600">
                We sent a 6-digit code to{' '}
                <span className="font-semibold text-slate-900">{email}</span>
              </p>
            ) : (
              <p className="text-sm text-slate-600">Enter the 6-digit code from your email</p>
            )}
          </div>

          {/* Spam notice */}
          <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <span className="text-amber-500 text-base leading-tight">💡</span>
            <p className="text-xs text-amber-800 leading-relaxed">
              Can't find the email? Check your <span className="font-semibold">spam or junk folder</span> — OTP emails sometimes land there.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {/* 6-digit boxes */}
          <div className="flex gap-2 justify-center">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={(e) => e.target.select()}
                disabled={loading}
                className={`
                  w-11 h-14 text-center text-xl font-semibold rounded-lg border-2
                  transition-all duration-150
                  focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${digit
                    ? 'border-sky-400 bg-sky-50 text-sky-700'
                    : 'border-slate-400 bg-slate-50 text-slate-900'
                  }
                `}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying…
            </div>
          )}

          {!loading && currentCode.length > 0 && currentCode.length < OTP_LENGTH && (
            <p className="text-center text-xs text-slate-400">
              {OTP_LENGTH - currentCode.length} digit{OTP_LENGTH - currentCode.length !== 1 ? 's' : ''} remaining
            </p>
          )}

          {/* Resend */}
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resending || loading}
              className="inline-flex items-center gap-1.5 text-sm text-sky-600 font-medium
                         hover:underline disabled:text-slate-400 disabled:no-underline
                         disabled:cursor-not-allowed transition-colors"
            >
              {resending ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
              ) : cooldown > 0 ? (
                `Resend code in ${cooldown}s`
              ) : (
                <><RotateCcw className="w-3.5 h-3.5" /> Resend code</>
              )}
            </button>
          </div>
        </div>

        <p className="text-center text-sm text-sky-100 mt-6">
          Wrong account?{' '}
          <button
            onClick={() => { clearTempToken(); navigate('/login') }}
            className="text-white font-semibold hover:underline"
          >
            Back to sign in
          </button>
        </p>
      </div>
    </div>
  )
}
